/**
 * User profile service — Firestore operations for user documents.
 */
// @ts-check
import { doc } from '@/lib/firebase';
import { onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { isMarketingOptInGranted } from '@/services/userServiceActions';
import {
  DEFAULT_TIER,
  DEV_TIER_CHANGED_EVENT,
  USERS_COLLECTION,
  VALID_TIERS,
  devTierStorageKey,
  isDevTierLocalOverrideEnabled,
} from '@/services/userServiceConstants';

export {
  setUserTier,
  markHintSeen,
  unmarkHintSeen,
  registerWallInterest,
  isMarketingOptInGranted,
  setUserMarketingOptIn,
  setUserProfileDisplayName,
  incrementLoginCount,
} from '@/services/userServiceActions';

/** @typedef {'free' | 'lite' | 'pro'} UserTier */

export { DEFAULT_TIER };

/**
 * Normaliseert een tier-waarde uit Firestore; ontbrekend/ongeldig → `'free'`.
 * @param {unknown} tier
 * @returns {UserTier}
 */
export function normalizeTier(tier) {
  return typeof tier === 'string' && VALID_TIERS.has(tier)
    ? /** @type {UserTier} */ (tier)
    : DEFAULT_TIER;
}

/**
 * @typedef {Object} UserProfileSnapshot
 * @property {UserTier} tier
 * @property {string[]} seenHints
 * @property {number} loginCount
 * @property {Date | null} createdAt
 * @property {string} displayName
 * @property {boolean} marketingOptInGranted
 */

/**
 * @param {Record<string, unknown>} [data]
 * @returns {Date | null}
 */
export function normalizeProfileCreatedAt(data) {
  const raw = data?.createdAt;
  if (!raw) return null;
  if (raw instanceof Date) return raw;
  if (typeof raw === 'object' && raw !== null && 'toDate' in raw) {
    const converted = /** @type {{ toDate: () => Date }} */ (raw).toDate();
    return Number.isNaN(converted.getTime()) ? null : converted;
  }
  if (typeof raw === 'string' || typeof raw === 'number') {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

/**
 * @param {Record<string, unknown>} [data]
 * @returns {string[]}
 */
export function normalizeSeenHints(data) {
  const raw = data?.seenHints;
  if (!Array.isArray(raw)) return [];
  return raw.filter((hint) => typeof hint === 'string');
}

/**
 * @param {Record<string, unknown>} [data]
 * @returns {string}
 */
export function normalizeProfileDisplayName(data) {
  const raw = data?.displayName;
  return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {UserProfileSnapshot}
 */
function profileFromSnapshot(docSnap) {
  const data = docSnap.exists() ? docSnap.data() : {};
  return {
    tier: normalizeTier(data.tier),
    seenHints: normalizeSeenHints(data),
    loginCount:
      typeof data.loginCount === 'number' && Number.isFinite(data.loginCount)
        ? data.loginCount
        : 0,
    createdAt: normalizeProfileCreatedAt(data),
    displayName: normalizeProfileDisplayName(data),
    marketingOptInGranted: isMarketingOptInGranted(data),
  };
}

/**
 * Real-time subscription op het user-profiel (tier, seenHints, loginCount).
 * @param {string} userId
 * @param {(profile: UserProfileSnapshot) => void} onUpdate
 * @param {(error: Error) => void} [onError]
 * @returns {() => void}
 */
export function subscribeToUserProfile(userId, onUpdate, onError) {
  if (!userId) return () => {};

  const userRef = doc(db, USERS_COLLECTION, userId);
  /** @type {UserProfileSnapshot} */
  let lastProfile = {
    tier: DEFAULT_TIER,
    seenHints: [],
    loginCount: 0,
    createdAt: null,
    displayName: '',
    marketingOptInGranted: false,
  };

  const emitProfile = (profile) => {
    lastProfile = profile;
    onUpdate(profile);
  };

  const applyProfileFromSnapshot = (docSnap) => {
    let profile = profileFromSnapshot(docSnap);
    if (isDevTierLocalOverrideEnabled()) {
      const stored = localStorage.getItem(devTierStorageKey(userId));
      if (stored && VALID_TIERS.has(stored)) {
        profile = { ...profile, tier: /** @type {UserTier} */ (stored) };
      }
    }
    emitProfile(profile);
  };

  const handleDevTierChange = (event) => {
    if (event.detail?.userId === userId) {
      emitProfile({
        ...lastProfile,
        tier: normalizeTier(event.detail.tier),
      });
    }
  };

  if (isDevTierLocalOverrideEnabled()) {
    window.addEventListener(DEV_TIER_CHANGED_EVENT, handleDevTierChange);
  }

  const unsubscribe = onSnapshot(userRef, applyProfileFromSnapshot, (error) => {
    console.error(
      `[UserService] Error listening to profile for ${userId}:`,
      error,
    );
    if (onError) onError(error);
  });

  return () => {
    unsubscribe();
    if (isDevTierLocalOverrideEnabled()) {
      window.removeEventListener(DEV_TIER_CHANGED_EVENT, handleDevTierChange);
    }
  };
}

/**
 * Real-time subscription op `users/{userId}.tier`.
 * @param {string} userId
 * @param {(tier: UserTier) => void} onUpdate
 * @param {(error: Error) => void} [onError]
 * @returns {() => void}
 */
export function subscribeToUserTier(userId, onUpdate, onError) {
  return subscribeToUserProfile(userId, ({ tier }) => onUpdate(tier), onError);
}
